import React, { useState } from 'react';
import { EmailProvider, useEmailConnectionsService, EMAIL_CONNECTIONS_UPDATED } from 'nekst-api';
import { Button, ButtonWithImage, ButtonColor, ButtonSize, LoadingAnimation, Text, TextLink } from 'ui-builder';
import { CardLayout, ErrorMessage, IconDeleteButton } from 'features/nekst-widgets';
import { useDataLoaderNew } from 'data-loader';
import gmailIcon from './gmail.png';

import styles from './GmailWidget.module.scss';

function PolicyLink() {
  return (
    <>
      <Text className={styles.text}>
        Refer to the 'Google Integrations (Calendar & Gmail)' section in our
        {' '}
        <TextLink onClick={() => {
          window.open('https://nekst.com/privacy', '_blank');
        }}>Privacy Policy</TextLink>
        {' '}
        to understand how your data is handled and protected.
      </Text>
    </>
  );
}

function ReauthenticateButton() {

  const emailConnectionsService = useEmailConnectionsService();

  const handleReauthenticate = async () => {
    // Delete existing connection first
    await emailConnectionsService.deleteConnection();

    // Start new OAuth flow
    const oauthResponse = await emailConnectionsService.getOAuthUrl(EmailProvider.GMAIL);
    window.location.href = oauthResponse.url;
  };

  return (
    <Button
      text="Re-authenticate"
      size={ButtonSize.SMALL}
      isFilled
      color={ButtonColor.YELLOW}
      onClick={handleReauthenticate}
    />
  );
}


function GmailConnection() {
  const [error, setError] = useState<string | null>(null);
  const emailConnectionsService = useEmailConnectionsService();

  const {
    data: connection,
    refreshData,
    loading,
  } = useDataLoaderNew(
    () => emailConnectionsService.getConnection(),
    {
      dependencies: [],
      events: [EMAIL_CONNECTIONS_UPDATED]
    }
  );

  const isConnected = connection !== null;

  const handleConnect = async () => {
    try {
      setError(null);

      const oauthResponse = await emailConnectionsService.getOAuthUrl(EmailProvider.GMAIL);
      window.location.href = oauthResponse.url;
    } catch (e) {
      setError('Failed to start connection. Please try again.');
    }
  };

  const handleDisconnect = async () => {
    try {
      await emailConnectionsService.deleteConnection();
      refreshData();
    } catch (e) {
      console.error('Failed to disconnect:', e);
      setError('Failed to disconnect. Please try again.');
    }
  };


  const getGmailIcon = () => {
    return <img src={gmailIcon} alt="Gmail" width="24" height="24" />;
  };

  const formatConnectionDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (isConnected && connection) {
    return (
      <>
        <CardLayout
          icon={getGmailIcon()}
          title={
            <div className={styles.titleWithStatus}>
              <span>Gmail Connected</span>
              {!connection.isValid && (
                <span className={styles.statusBadge}>
                  ⚠️ Needs Attention
                </span>
              )}
            </div>
          }
          subtitle={
            <div>
              <Text>{connection.email}</Text>

              {connection.isValid && (
                <Text>
                  {' • '}
                  <Text>Connected on: {formatConnectionDate(connection.createdAt)}</Text>
                </Text>
              )}

              {!connection.isValid && (
                <div className={styles.warningSection}>
                  <div className={styles.warningMessage}>
                    <span className={styles.warningIcon}>⚠️</span>
                    <Text className={styles.warningText}>
                      Authentication expired. You need to re-authenticate to continue sending emails.
                    </Text>
                  </div>

                </div>
              )}
            </div>
          }
          actions={(
            <>
              {connection.isValid && (<IconDeleteButton
                message="Are you sure you want to disconnect your Gmail account? You will need to reconnect to continue sending emails through Gmail."
                deleteFunc={handleDisconnect}
              />)}
              {!connection.isValid && (<ReauthenticateButton />)}
            </>
          )}
        />
        {error && (
          <ErrorMessage>{error}</ErrorMessage>
        )}
      </>
    );
  } else {
    if (!loading) {
      return (
        <>
          <CardLayout
            icon={getGmailIcon()}
            title="Gmail"
            subtitle="Connect your Gmail account to send emails directly through your personal email address"
            actions={
              <ButtonWithImage
                image={getGmailIcon()}
                text="Connect"
                size={ButtonSize.MEDIUM}
                color={ButtonColor.GREEN}
                onClick={handleConnect}
              />
            }
          />
          {error && (
            <ErrorMessage>{error}</ErrorMessage>
          )}
        </>
      );
    } else {
      return (<LoadingAnimation />)
    }
  }


}

export default function GmailWidget() {
  return (
    <>
      <GmailConnection />
      <PolicyLink />
    </>
  );
}
